/**
 * Conversation export for the CS dashboard.
 *
 * Given a `TopConversation` row (which carries the conversation id + the user
 * sub, both derived from the S3 trace key during the rollup), this fetches the
 * raw `trace.jsonl` from the client's outputs bucket and packages it as either:
 *
 *   - `.jsonl` — the raw, lossless trace exactly as stored (includes thinking,
 *     result/cost events, stream deltas). The "give me everything" option.
 *   - `.txt`   — a readable transcript rendered via `renderTraceText`.
 *
 * Because the rollup derives `user_id` + `conversation_id` from the trace key,
 * recombining them here is guaranteed to address the same object.
 *
 * A trace can pass the 6 MB one Lambda call carries, so the client access
 * Lambda (TASK-625) returns a short-lived link to the one object, and the
 * browser fetches it. The client's outputs bucket allows GET from the
 * portal's address.
 */
import { clientAccess } from '@/services/clientAccess';
import { renderTraceText, type ConversationTraceMeta } from '@/utils/traceParsing';
import type { ClientAccountRef } from '@/types/clientAccount';
import type { TopConversation } from '@/types/fleetAnalytics';
import type { ToolResultFile } from '@/types/tools';

export type ConversationExportFormat = 'jsonl' | 'txt';

/**
 * The raw trace.jsonl text of one conversation in a client's outputs bucket.
 * The Lambda checks the trace is there and signs a link that reads only that
 * object; the browser then fetches the link.
 */
export async function fetchTraceText(clientName: string, userSub: string, conversationId: string): Promise<string> {
  const { url } = await clientAccess<{ url: string; bytes: number }>('conversations.traceUrl', {
    clientName,
    userSub,
    conversationId,
  });
  const res = await fetch(url);
  if (res.status === 403 || res.status === 404) {
    throw new Error('No trace found for this conversation (it may have been purged or never written).');
  }
  if (!res.ok) throw new Error(`The trace did not download (HTTP ${res.status}). Try again.`);
  return await res.text();
}

/** Fetch the raw trace.jsonl text for a conversation from the client account. */
export async function fetchRawTrace(ref: ClientAccountRef, userSub: string, conversationId: string): Promise<string> {
  return fetchTraceText(ref.clientName, userSub, conversationId);
}

/** Short, filesystem-safe slice of a conversation id for filenames. */
function shortConvId(conversationId: string): string {
  return (conversationId || 'conversation').replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 20) || 'conversation';
}

function stamp(): string {
  return new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
}

/**
 * Fetch + package a conversation as a downloadable file. Returns a
 * `ToolResultFile` ready for `FileExportService.downloadFile`.
 */
export async function fetchConversationExport(
  ref: ClientAccountRef,
  conv: TopConversation,
  format: ConversationExportFormat,
  opts?: { userEmail?: string }
): Promise<ToolResultFile> {
  if (!conv.user_id) {
    throw new Error('Conversation is missing a user id — cannot locate its trace.');
  }
  const raw = await fetchRawTrace(ref, conv.user_id, conv.conversation_id);
  const base = `${ref.clientName}-conv-${shortConvId(conv.conversation_id)}-${stamp()}`;

  if (format === 'jsonl') {
    return {
      name: `${base}.jsonl`,
      content: raw,
      mimeType: 'application/x-ndjson',
      size: new Blob([raw]).size,
    };
  }

  const meta: ConversationTraceMeta = {
    clientName: ref.clientName,
    conversationId: conv.conversation_id,
    userId: conv.user_id,
    userEmail: opts?.userEmail,
    agentTitle: conv.agent_title,
    agentId: conv.agent_id,
    model: conv.model,
    isScheduled: conv.is_scheduled,
    startedAt: conv.started_at,
    lastRequestAt: conv.last_request_at,
    totalCostUsd: conv.total_cost_usd,
    totalTurns: conv.total_turns,
    requestCount: conv.request_count,
    toolCallCount: conv.tool_call_count,
    inputTokens: conv.input_tokens,
    outputTokens: conv.output_tokens,
    exportedAt: new Date().toISOString(),
  };
  const text = renderTraceText(raw, meta);
  return {
    name: `${base}.txt`,
    content: text,
    mimeType: 'text/plain',
    size: new Blob([text]).size,
  };
}
